import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Account,
  AccountListResponse,
  AccountViewModel,
  ApiResponse,
} from '@/api/types'

/**
 * Account management service (Chart of Accounts)
 */
export class AccountsService {
  /**
   * Get paginated list of accounts for a branch
   */
  async getAllAccounts(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<AccountListResponse> {
    const url = endpoints.accounts.getAll(branchId, pageNumber, pageSize)
    console.log('🔵 Calling getAllAccounts API:', url)
    const response = await apiClient.get<ApiResponse<AccountListResponse> | AccountListResponse | any>(url)
    console.log('🔵 API Response:', response)
    const payload = response.data
    console.log('🔵 Response payload:', payload)

    // Helper to map accounts array
    const mapAccounts = (accounts: any[]): Account[] => {
      return accounts.map(acc => this.mapAccountData(acc))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapAccounts(data),
          }
        }
        return data as AccountListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapAccounts(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapAccounts(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapAccounts(payload.data) : [],
    }
  }

  /**
   * Get account by ID (for edit)
   */
  async getAccountById(branchId: number, accountId: number): Promise<Account> {
    const url = endpoints.accounts.getById(accountId, branchId)
    const response = await apiClient.get<ApiResponse<Account> | Account | any>(url)
    
    let accountData: any
    
    if ('data' in response.data && 'success' in response.data) {
      accountData = response.data.data
    } else if ('data' in response.data) {
      accountData = response.data.data
    } else {
      accountData = response.data
    }
    
    return this.mapAccountData(accountData)
  }

  /**
   * Get accounts by parent ID (for tree structure)
   */
  async getAccountsByParentId(parentId: number, branchId: number): Promise<Account[]> {
    const url = endpoints.accounts.getByParentId(parentId, branchId)
    const response = await apiClient.get<ApiResponse<Account[]> | Account[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data.map(acc => this.mapAccountData(acc))
    }
    
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data.map((acc: any) => this.mapAccountData(acc))
    }
    
    return []
  }

  /**
   * Generate account code based on parent
   */
  async generateAccountCode(parentId: number): Promise<string> {
    const url = endpoints.accounts.generateCode(parentId)
    console.log('🔵 [GenerateCode API] Request URL:', url)
    console.log('🔵 [GenerateCode API] ParentId being sent:', parentId)
    console.log('🔵 [GenerateCode API] ParentId type:', typeof parentId)
    
    try {
      const response = await apiClient.get<ApiResponse<string> | string | any>(url)
      console.log('🔵 [GenerateCode API] Full Response:', response)
      console.log('🔵 [GenerateCode API] Response Data:', response.data)
      console.log('🔵 [GenerateCode API] Response Data Type:', typeof response.data)
      
      // Check if response.data is a string directly
      if (typeof response.data === 'string') {
        console.log('🔵 [GenerateCode API] Returning string directly:', response.data)
        return response.data
      }
      
      // Check if response has accountCode property (most common case)
      if (response.data && typeof response.data === 'object' && 'accountCode' in response.data) {
        const code = response.data.accountCode
        console.log('🔵 [GenerateCode API] Found accountCode property:', code)
        return typeof code === 'string' ? code : String(code)
      }
      
      // Check if response has data property with accountCode
      if ('data' in response.data && response.data.data && typeof response.data.data === 'object' && 'accountCode' in response.data.data) {
        const code = response.data.data.accountCode
        console.log('🔵 [GenerateCode API] Found accountCode in response.data.data:', code)
        return typeof code === 'string' ? code : String(code)
      }
      
      // Check if response has data property (string or object)
      if ('data' in response.data) {
        const data = response.data.data
        if (typeof data === 'string') {
          console.log('🔵 [GenerateCode API] Returning from response.data.data (string):', data)
          return data
        }
        if (data && typeof data === 'object' && 'accountCode' in data) {
          const code = data.accountCode
          console.log('🔵 [GenerateCode API] Returning accountCode from nested data:', code)
          return typeof code === 'string' ? code : String(code)
        }
      }
      
      // Check for success wrapper
      if ('success' in response.data && 'data' in response.data) {
        const data = response.data.data
        if (typeof data === 'string') {
          console.log('🔵 [GenerateCode API] Returning from success wrapper (string):', data)
          return data
        }
        if (data && typeof data === 'object' && 'accountCode' in data) {
          const code = data.accountCode
          console.log('🔵 [GenerateCode API] Returning accountCode from success wrapper:', code)
          return typeof code === 'string' ? code : String(code)
        }
      }
      
      console.warn('⚠️ [GenerateCode API] No code found in response, returning empty string')
      console.warn('⚠️ [GenerateCode API] Response structure:', JSON.stringify(response.data, null, 2))
      return ''
    } catch (error: any) {
      console.error('❌ [GenerateCode API] Error:', error)
      console.error('❌ [GenerateCode API] Error Response:', error.response)
      console.error('❌ [GenerateCode API] Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Save account (insert)
   */
  async saveAccount(account: AccountViewModel): Promise<Account> {
    const url = endpoints.accounts.save
    
    // Map AccountViewModel to API structure
    // Note: accountLevel is calculated by backend, but we can send 0 or calculate from parent
    const apiPayload = {
      accountId: account.accountId || 0,
      accountNameEn: account.name || '',
      accountNameAr: account.nameAr || '',
      accountCode: account.code || '',
      parentAccountId: account.parentAccountId || 0,
      defaultType: account.defaultType || '',
      accountLevel: 0, // Backend will calculate based on parent
      branchId: account.branchId || 0,
      isTransactionable: account.isTransactionable || false,
    }
    
    console.log('🔵 [SaveAccount API] Request URL:', url)
    console.log('🔵 [SaveAccount API] Original Payload:', account)
    console.log('🔵 [SaveAccount API] Mapped API Payload:', apiPayload)
    console.log('🔵 [SaveAccount API] Mapped Payload JSON:', JSON.stringify(apiPayload, null, 2))
    
    try {
      const response = await apiClient.post<ApiResponse<Account> | Account>(
        url,
        apiPayload
      )
      
      console.log('🔵 [SaveAccount API] Full Response:', response)
      console.log('🔵 [SaveAccount API] Response Status:', response.status)
      console.log('🔵 [SaveAccount API] Response Data:', response.data)
      console.log('🔵 [SaveAccount API] Response Data Type:', typeof response.data)
      
      if ('data' in response.data && 'success' in response.data) {
        console.log('🔵 [SaveAccount API] Response has success wrapper')
        const mapped = this.mapAccountData((response.data as ApiResponse<Account>).data)
        console.log('🔵 [SaveAccount API] Mapped account data:', mapped)
        return mapped
      }
      
      console.log('🔵 [SaveAccount API] Response is direct account data')
      const mapped = this.mapAccountData(response.data as Account)
      console.log('🔵 [SaveAccount API] Mapped account data:', mapped)
      return mapped
    } catch (error: any) {
      console.error('❌ [SaveAccount API] Error:', error)
      console.error('❌ [SaveAccount API] Error Response:', error.response)
      console.error('❌ [SaveAccount API] Error Response Data:', error.response?.data)
      console.error('❌ [SaveAccount API] Error Response Status:', error.response?.status)
      throw error
    }
  }

  /**
   * Update account
   */
  async updateAccount(account: AccountViewModel): Promise<Account> {
    const url = endpoints.accounts.update
    
    // Map AccountViewModel to API structure
    // Note: accountLevel is calculated by backend, but we can send 0 or calculate from parent
    const apiPayload = {
      accountId: account.accountId || 0,
      accountNameEn: account.name || '',
      accountNameAr: account.nameAr || '',
      accountCode: account.code || '',
      parentAccountId: account.parentAccountId || 0,
      defaultType: account.defaultType || '',
      accountLevel: 0, // Backend will calculate based on parent
      branchId: account.branchId || 0,
      isTransactionable: account.isTransactionable || false,
    }
    
    console.log('🔵 [UpdateAccount API] Request URL:', url)
    console.log('🔵 [UpdateAccount API] Original Payload:', account)
    console.log('🔵 [UpdateAccount API] Mapped API Payload:', apiPayload)
    console.log('🔵 [UpdateAccount API] Mapped Payload JSON:', JSON.stringify(apiPayload, null, 2))
    
    try {
      const response = await apiClient.put<ApiResponse<Account> | Account>(
        url,
        apiPayload
      )
      
      console.log('🔵 [UpdateAccount API] Full Response:', response)
      console.log('🔵 [UpdateAccount API] Response Status:', response.status)
      console.log('🔵 [UpdateAccount API] Response Data:', response.data)
      console.log('🔵 [UpdateAccount API] Response Data Type:', typeof response.data)
      
      if ('data' in response.data && 'success' in response.data) {
        console.log('🔵 [UpdateAccount API] Response has success wrapper')
        const mapped = this.mapAccountData((response.data as ApiResponse<Account>).data)
        console.log('🔵 [UpdateAccount API] Mapped account data:', mapped)
        return mapped
      }
      
      console.log('🔵 [UpdateAccount API] Response is direct account data')
      const mapped = this.mapAccountData(response.data as Account)
      console.log('🔵 [UpdateAccount API] Mapped account data:', mapped)
      return mapped
    } catch (error: any) {
      console.error('❌ [UpdateAccount API] Error:', error)
      console.error('❌ [UpdateAccount API] Error Response:', error.response)
      console.error('❌ [UpdateAccount API] Error Response Data:', error.response?.data)
      console.error('❌ [UpdateAccount API] Error Response Status:', error.response?.status)
      throw error
    }
  }

  /**
   * Upload Excel file
   */
  async uploadExcel(branchId: number, file: File): Promise<any> {
    const url = endpoints.accounts.uploadExcel(branchId)
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    
    return response.data
  }

  /**
   * Download chart of accounts
   */
  async downloadChartOfAccounts(branchId: number): Promise<Blob> {
    const url = endpoints.accounts.downloadChart(branchId)
    const response = await apiClient.get(url, {
      responseType: 'blob',
    })
    
    return response.data
  }

  /**
   * Get transactionable accounts for a branch
   */
  async getTransactionableAccounts(branchId: number): Promise<Account[]> {
    const url = endpoints.accounts.getTransactionableAccounts(branchId)
    const response = await apiClient.get<ApiResponse<Account[]> | Account[]>(url)
    
    // Handle both wrapped and direct responses
    if (Array.isArray(response.data)) {
      return response.data.map(acc => this.mapAccountData(acc))
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data.map(acc => this.mapAccountData(acc))
    }
    return []
  }

  /**
   * Map API response to Account interface
   * API returns: accountNameEn, accountNameAr, accountCode, accountLevel
   */
  private mapAccountData(accountData: any): Account {
    return {
      accountId: accountData.accountId || accountData.id,
      id: accountData.id || accountData.accountId,
      accountNature: accountData.accountNature || 0,
      parentAccountId: accountData.parentAccountId || accountData.parentId || 0,
      parentId: accountData.parentId || accountData.parentAccountId || 0,
      // API returns accountNameEn, accountNameAr, accountCode, accountLevel
      name: accountData.accountNameEn || accountData.nameEn || accountData.name || '',
      nameEn: accountData.accountNameEn || accountData.nameEn || accountData.name || '',
      nameAr: accountData.accountNameAr || accountData.nameAr || '',
      code: accountData.accountCode || accountData.code || '',
      defaultType: accountData.defaultType || '',
      isTransactionable: accountData.isTransactionable || false,
      level: accountData.accountLevel || accountData.level || 0,
      branchId: accountData.branchId,
      companyId: accountData.companyId,
    }
  }
}

export const accountsService = new AccountsService()

